'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import { twMerge } from 'tailwind-merge';
import { useState, useEffect } from 'react';
import Box from '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const THUMB_STYLE = `
  [&::-moz-range-thumb]:pointer-events-auto
  [&::-moz-range-thumb]:mr-2
  [&::-moz-range-thumb]:-translate-x-1/4
  [&::-moz-range-thumb]:transform
  [&::-moz-range-thumb]:appearance-none
  [&::-moz-range-thumb]:rounded-full
  [&::-moz-range-thumb]:border
  [&::-moz-range-thumb]:border-w-gray-200
  [&::-moz-range-thumb]:bg-transparent
  [&::-moz-range-thumb]:bg-white
  [&::-moz-range-thumb]:shadow-graymedium
  [&::-moz-range-thumb]:outline
  [&::-moz-range-thumb]:ring-1
  [&::-moz-range-thumb]:ring-inset
  [&::-moz-range-thumb]:ring-w-gray-200
  [&::-moz-range-thumb]:transition-all
  [&::-moz-range-thumb]:duration-150
  [&::-moz-range-thumb]:ease-in-out
  [&::-moz-range-thumb]:dark:bg-w-white

  [&::-webkit-slider-thumb]:pointer-events-auto
  [&::-webkit-slider-thumb]:mr-2
  [&::-webkit-slider-thumb]:-translate-x-1/4
  [&::-webkit-slider-thumb]:transform
  [&::-webkit-slider-thumb]:appearance-none
  [&::-webkit-slider-thumb]:rounded-full
  [&::-webkit-slider-thumb]:border
  [&::-webkit-slider-thumb]:border-w-gray-200
  [&::-webkit-slider-thumb]:bg-transparent
  [&::-webkit-slider-thumb]:bg-white
  [&::-webkit-slider-thumb]:shadow-graymedium
  [&::-webkit-slider-thumb]:outline
  [&::-webkit-slider-thumb]:ring-1
  [&::-webkit-slider-thumb]:ring-inset
  [&::-webkit-slider-thumb]:ring-w-gray-200
  [&::-webkit-slider-thumb]:transition-all
  [&::-webkit-slider-thumb]:duration-150
  [&::-webkit-slider-thumb]:ease-in-out
  [&::-webkit-slider-thumb]:dark:bg-w-white
  `;
/**
 * 단일 또는 이중 슬라이더를 제공하는 범위 선택 컴포넌트입니다.
 *
 * @component
 *
 * @param {Object} props
 * @param {('single'|'dual')} [props.variant='single'] - 슬라이더 유형 (단일 또는 이중)
 * @param {('sm'|'md')} [props.size='sm'] - 슬라이더 크기
 * @param {number} [props.step=10] - 슬라이더 단계 값
 * @param {{min: number, max: number}} [props.value={min: 0, max: 0}] - 슬라이더의 현재 값 (0~100 사이)
 * @param {number} [props.min=0] - 최소값
 * @param {number} [props.max=100] - 최대값
 * @param {(value: {min: number, max: number}) => void} [props.onChange] - 값 변경 시 호출되는 콜백 함수
 * @param {string} [props.className] - 추가 CSS 클래스
 *
 * @example
 * // 단일 슬라이더
 * <RangeSlider
 *   variant="single"
 *   min={0}
 *   max={100}
 *   step={10}
 *   value={{ min: 0, max: 50 }}
 *   onChange={(value) => console.log(value)}
 * />
 *
 * // 이중 슬라이더
 * <RangeSlider
 *   variant="dual"
 *   min={0}
 *   max={100}
 *   step={10}
 *   value={{ min: 30, max: 70 }}
 *   onChange={(value) => console.log(value)}
 * />
 */ const RangeSlider = (_param)=>{
    var { variant = 'single', size = 'sm', step = 10, value = {
        min: 0,
        max: 0
    }, min = 0, max = 100, onChange, className } = _param, rest = _object_without_properties(_param, [
        "variant",
        "size",
        "step",
        "value",
        "min",
        "max",
        "onChange",
        "className"
    ]);
    const isDual = variant === 'dual';
    const { min: initialMin, max: maxInputValue } = value;
    const minInputValue = isDual ? initialMin : 0;
    const [rangeValue, setRangeValue] = useState({
        min: minInputValue,
        max: maxInputValue
    });
    const [clickedThumb, setClickedThumb] = useState('end');
    const convertToPercent = (value)=>{
        if (max === min) return 0;
        return (value - min) / (max - min) * 100;
    };
    const handleInput = (e, key)=>{
        const isStartThumb = key === 'min';
        const recentThumb = isStartThumb ? 'start' : 'end';
        const inputValue = Number(e.target.value);
        const newValue = isStartThumb ? Math.min(inputValue, rangeValue.max - step) : Math.max(inputValue, rangeValue.min + step);
        setClickedThumb(recentThumb);
        setRangeValue((prev)=>_object_spread_props(_object_spread({}, prev), {
                [key]: newValue
            }));
        onChange === null || onChange === void 0 ? void 0 : onChange(_object_spread_props(_object_spread({}, rangeValue), {
            [key]: newValue
        }));
    };
    const generateRangeSize = ()=>isDual ? convertToPercent(rangeValue.max) - convertToPercent(rangeValue.min) : convertToPercent(rangeValue.max);
    const generateThumbSize = ()=>{
        if (size === 'sm') return THUMB_STYLE + '[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4';
        return THUMB_STYLE + '[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5';
    };
    const dotCount = Math.floor(Number(max) / Number(step)) + 1;
    useEffect(()=>{
        setRangeValue(_object_spread_props(_object_spread({}, rangeValue), {
            max: value.max
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        value.max
    ]);
    return /*#__PURE__*/ jsxs(Box, {
        className: twMerge('relative px-1', className),
        "data-testid": "design-system-rangeSlider",
        children: [
            isDual && /*#__PURE__*/ jsx("input", _object_spread({
                "data-testid": "design-system-rangeSlider--start-thumb-input",
                type: "range",
                min: min,
                max: max,
                step: step,
                value: rangeValue.min,
                onInput: (event)=>handleInput(event, 'min'),
                className: twMerge('pointer-events-none absolute z-30 h-full w-full cursor-grab appearance-none rounded-full bg-transparent text-transparent', generateThumbSize(), clickedThumb === 'start' && 'z-50')
            }, rest)),
            /*#__PURE__*/ jsx("input", _object_spread({
                "data-testid": "design-system-rangeSlider--end-thumb-input",
                type: "range",
                min: min,
                max: max,
                value: rangeValue.max,
                onInput: (event)=>handleInput(event, 'max'),
                step: step,
                className: twMerge('pointer-events-auto absolute z-30 h-full w-full cursor-grab appearance-none rounded-full bg-transparent text-transparent', generateThumbSize(), isDual && 'pointer-events-none', clickedThumb === 'end' && 'z-50')
            }, rest)),
            /*#__PURE__*/ jsxs(Box, {
                "data-testid": "design-system-rangeSlider--container",
                className: twMerge('relative overflow-hidden', size === 'sm' && 'h-1 rounded-sm', size === 'md' && 'h-2 rounded'),
                children: [
                    size === 'md' && /*#__PURE__*/ jsx(Box, {
                        className: "absolute z-20 flex h-2 w-full items-center justify-between bg-transparent p-0.5",
                        children: [
                            ...Array(dotCount)
                        ].map((_, index)=>/*#__PURE__*/ jsx(Box, {
                                "data-testid": "design-system-rangeSlider--unit-dot",
                                className: "size-1 rounded-full bg-w-white"
                            }, index))
                    }),
                    /*#__PURE__*/ jsx(Box, {
                        className: 'absolute left-0 top-0 z-0 h-full w-full bg-w-gray-50',
                        "data-testid": "design-system-rangeSlider--rail"
                    }),
                    /*#__PURE__*/ jsx(Box, {
                        className: 'absolute left-0 top-0 z-10 h-full bg-primary',
                        "data-testid": "design-system-rangeSlider--fill-track",
                        style: {
                            width: `${generateRangeSize()}%`,
                            left: isDual ? `${convertToPercent(rangeValue.min)}%` : 0
                        }
                    })
                ]
            })
        ]
    });
};

export { RangeSlider as default };
